import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { restoreDetailText, restoreVersionRowText } from "../pages/DaySchedule/CustomPrintout/customPrintoutText";
import type { CustomPrintoutDetail } from "../pages/DaySchedule/CustomPrintout/customPrintoutTypes";
import { backendService } from "../services/backend.service";
import { customPrintoutKeys, storeDetail, storeListRow } from "./useCustomPrintouts";

/** A disabled query's key: it never runs, so it only has to be stable. */
const NOTHING_CHOSEN_KEY = "none";

/**
 * A printout's versions, newest first, without their sources. Idle until `enabled` — nobody needs the history until
 * they open it — and while no saved printout is shown.
 */
export const useCustomPrintoutVersions = (printoutId: string | null, enabled: boolean) => {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	return useQuery({
		queryKey: customPrintoutKeys.versions(departmentId, printoutId === null ? NOTHING_CHOSEN_KEY : printoutId),
		queryFn: async () => {
			if (printoutId === null) throw new Error("useCustomPrintoutVersions ran with no printout chosen");
			return (await backendService.listCustomPrintoutVersions(departmentId, printoutId)).data.map(restoreVersionRowText);
		},
		enabled: enabled && printoutId !== null,
	});
};

/**
 * One version of a printout with its source and conversation; idle while none is asked for. A version never changes
 * once saved (restoring makes a new one), so what was fetched stays good for the session.
 */
export const useCustomPrintoutVersion = (printoutId: string | null, versionId: string | null) => {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	return useQuery({
		queryKey: customPrintoutKeys.version(
			departmentId,
			printoutId === null ? NOTHING_CHOSEN_KEY : printoutId,
			versionId === null ? NOTHING_CHOSEN_KEY : versionId,
		),
		queryFn: async () => {
			if (printoutId === null || versionId === null) throw new Error("useCustomPrintoutVersion ran with no version chosen");
			return restoreDetailText((await backendService.getCustomPrintoutVersion(departmentId, printoutId, versionId)).data);
		},
		enabled: printoutId !== null && versionId !== null,
		staleTime: Number.POSITIVE_INFINITY,
	});
};

type RestoreVariables = { printoutId: string; versionId: string; versionNumber: number };

/**
 * Restores an earlier version: the server appends a copy of it as the new current version. The answer is that new
 * version, so it goes straight into the printout's cache and its list row — the version chip, list and preview switch
 * together, before the toast — and the list and the history reload anyway. A
 * failure is shown and tracked by `showErrorSnackbar`, in the server's words when it sent an error key.
 */
export const useRestoreCustomPrintoutVersion = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();
	const departmentId = chosenDepartment.department_id;
	const eventProps = ({ printoutId, versionNumber }: RestoreVariables) => ({
		department_id: departmentId,
		printout_id: printoutId,
		restored_version_number: versionNumber,
	});
	return useMutation<CustomPrintoutDetail, unknown, RestoreVariables>({
		mutationFn: async ({ printoutId, versionId }) =>
			restoreDetailText((await backendService.restoreCustomPrintoutVersion(printoutId, versionId, { departmentId })).data),
		onMutate: (variables) => track("custom_printout_version_restore_start", eventProps(variables)),
		onSuccess: (detail, variables) => {
			storeDetail(queryClient, departmentId, detail);
			storeListRow(queryClient, departmentId, detail);
			void queryClient.invalidateQueries({ queryKey: customPrintoutKeys.list(departmentId) });
			track("custom_printout_version_restore_success", { ...eventProps(variables), new_version_number: detail.versionNumber });
		},
		onError: (error, variables) => {
			showErrorSnackbar(t("printout.custom.ai.version.restore.failed"), { event: "custom_printout_version_restore_error", ...eventProps(variables) }, error);
		},
	});
};
